/**
 * Empty, error and loading states.
 *
 * The master prompt requires every API-driven screen to have all three; the
 * approved artwork designed none of them (gap G-11). These are built in the
 * same visual language — cream ground, green accent, restrained type — so they
 * do not read as foreign when they appear.
 */

import { memo } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';

import { colors, space } from '../../theme';
import { Button } from '../common/Button';
import { Text } from '../common/Text';
import { useT } from '../../core/i18n/LanguageProvider';

interface BaseProps {
  title: string;
  message?: string;
  testID?: string;
}

export const EmptyState = memo(function EmptyState({
  title,
  message,
  actionLabel,
  onAction,
  testID = 'empty-state',
}: BaseProps & { actionLabel?: string; onAction?: () => void }) {
  return (
    <View style={styles.container} testID={testID}>
      <Text variant="sectionTitle" align="center">
        {title}
      </Text>
      {message ? (
        <Text variant="body" tone="secondary" align="center" style={styles.message}>
          {message}
        </Text>
      ) : null}
      {actionLabel && onAction ? (
        <Button
          label={actionLabel}
          onPress={onAction}
          variant="secondary"
          fullWidth={false}
          style={styles.action}
        />
      ) : null}
    </View>
  );
});

export const ErrorState = memo(function ErrorState({
  title = 'Something went wrong',
  message = 'Please check your connection and try again.',
  onRetry,
  testID = 'error-state',
}: Partial<BaseProps> & { onRetry?: () => void }) {
  const t = useT();
  return (
    <View style={styles.container} testID={testID} accessibilityLiveRegion="polite">
      <Text variant="sectionTitle" align="center" tone="danger">
        {title}
      </Text>
      <Text variant="body" tone="secondary" align="center" style={styles.message}>
        {message}
      </Text>
      {onRetry ? (
        <Button
          label={t('Try Again')}
          onPress={onRetry}
          variant="secondary"
          fullWidth={false}
          style={styles.action}
          testID="retry-button"
        />
      ) : null}
    </View>
  );
});

export const LoadingState = memo(function LoadingState({
  label = 'Loading',
  testID = 'loading-state',
}: {
  label?: string;
  testID?: string;
}) {
  return (
    <View style={styles.container} testID={testID} accessibilityLabel={label}>
      <ActivityIndicator size="large" color={colors.text.brand} />
      <Text variant="caption" tone="tertiary" style={styles.message}>
        {label}
      </Text>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    paddingVertical: space['4xl'],
    paddingHorizontal: space.xl,
    alignItems: 'center',
    justifyContent: 'center',
    gap: space.sm,
  },
  message: {
    marginTop: space.xs,
  },
  action: {
    marginTop: space.lg,
  },
});
